import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { LeftOutlined, RightOutlined } from "@ant-design/icons";
import { Link } from "react-router-dom";
import "aos/dist/aos.css";
import BannerSkeleton from "./BannerSkeleton";

const HeroWrapper = styled.div`
  background: #f5f5f5;
  background-size: cover;
  background-position: center;
  background-attachment: scroll;
  background-repeat: no-repeat;
  min-height: 600px;
  display: flex;
  align-items: center;
  position: relative;
  overflow: hidden;
  transition: background-image 0.8s ease-in-out;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(255, 255, 255, 0);
    z-index: 0;
    animation: fadeInOverlay 0.8s ease-in-out;
  }

  @keyframes fadeInOverlay {
    from {
      opacity: 0.5;
    }
    to {
      opacity: 0;
    }
  }

  .container {
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 20px;
    width: 100%;
    display: flex;
    align-items: center;
    gap: 40px;
    position: relative;
    z-index: 2;
  }

  .hero-content {
    flex: 1;
    padding: 60px 0;
    animation: slideInLeft 0.6s ease-out;
    font-family: "Outfit", sans-serif;

    @keyframes slideInLeft {
      from {
        opacity: 0;
        transform: translateX(-30px);
      }
      to {
        opacity: 1;
        transform: translateX(0);
      }
    }

    &[data-slide-change] {
      animation: slideInLeft 0.6s ease-out;
    }

    .hero-subtitle {
      font-size: 13px;
      color: #666;
      margin-bottom: 30px;
      font-weight: 500;
      letter-spacing: 1px;
      text-transform: capitalize;
      font-family: "Outfit", sans-serif;
    }

    h1 {
      font-size: 4.2rem;
      font-weight: 800;
      color: #000;
      margin-bottom: 50px;
      line-height: 1.1;
      letter-spacing: -2px;
      white-space: pre-wrap;
      font-family: "Outfit", sans-serif;

      @media (max-width: 1024px) {
        font-size: 4rem;
      }

      @media (max-width: 768px) {
        font-size: 2.5rem;
        margin-bottom: 30px;
      }
    }

    .shop-now-link {
      display: inline-flex;
      align-items: center;
      gap: 12px;
      font-size: 13px;
      font-weight: 700;
      color: #000;
      text-decoration: none;
      letter-spacing: 2px;
      text-transform: uppercase;
      transition: all 0.3s ease;
      cursor: pointer;
      font-family: "Outfit", sans-serif;

      &:hover {
        gap: 18px;
        color: #fd8e07;
      }

      span {
        font-size: 16px;
        transition: transform 0.3s ease;
        display: inline-block;
      }

      &:hover span {
        transform: translateX(3px);
      }
    }
  }

  .carousel-nav {
    position: absolute;
    bottom: 30px;
    left: 50%;
    transform: translateX(-50%);
    display: flex;
    gap: 12px;
    z-index: 10;

    .dot {
      width: 12px;
      height: 12px;
      border-radius: 50%;
      background: #ddd;
      cursor: pointer;
      transition: all 0.3s ease;

      &.active {
        background: #000;
        width: 30px;
        border-radius: 6px;
      }

      &:hover {
        background: #999;
      }
    }
  }

  .arrow-nav {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    font-size: 24px;
    color: #000;
    cursor: pointer;
    padding: 12px 16px;
    transition: all 0.3s ease;
    z-index: 10;
    opacity: 0.6;

    &:hover {
      opacity: 1;
      transform: translateY(-50%) scale(1.1);
    }

    &.prev {
      left: 20px;
    }

    &.next {
      right: 20px;
    }

    @media (max-width: 768px) {
      display: none;
    }
  }

  @media (max-width: 768px) {
    min-height: 500px;
    padding: 0;

    &::before {
      background: rgba(255, 255, 255, 0);
    }

    .container {
      gap: 20px;
      padding: 0 20px;
      align-items: center;
    }

    .hero-content {
      padding: 40px 0;
      text-align: left;
      flex: 1;
      animation: slideInLeft 0.6s ease-out;

      .hero-subtitle {
        font-size: 11px;
        margin-bottom: 15px;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 1.5px;
        color: #666;
      }

      h1 {
        font-size: 2rem;
        margin-bottom: 20px;
        line-height: 1.1;
        font-weight: 800;
      }

      .shop-now-link {
        font-size: 12px;
      }
    }
  }

  @media (max-width: 576px) {
    min-height: 400px;

    .hero-content {
      padding: 30px 0;

      h1 {
        font-size: 1.6rem;
        margin-bottom: 15px;
      }

      .hero-subtitle {
        margin-bottom: 12px;
      }
    }
  }
`;

const HeroSection = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [slides, setSlides] = useState([]);
  const [loading, setLoading] = useState(true);

  // Fetch banners from API
  useEffect(() => {
    const fetchBanners = async () => {
      try {
        const resp = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/sidebar-banners`,
        );
        const data = await resp.json();
        if (data.status && data.data) {
          const formattedSlides = data.data.map((banner) => ({
            label: banner.heading,
            title: banner.sub_heading,
            image: banner.image_url,
          }));
          setSlides(formattedSlides);
        }
      } catch (error) {
        console.error("Failed to fetch banners:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchBanners();
  }, []);

  // Auto-switch slides every 2 seconds
  useEffect(() => {
    if (slides.length === 0) return;
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 2000);

    return () => clearInterval(interval);
  }, [slides.length]);

  const handlePrev = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  const handleNext = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };

  const handleDotClick = (index) => {
    setCurrentSlide(index);
  };

  const current = slides[currentSlide];

  // Show loading or empty state if no slides
  if (loading || slides.length === 0) {
    return <BannerSkeleton />;
  }

  return (
    <HeroWrapper style={{ backgroundImage: `url(${current.image})` }}>
      <div className="container">
        <div className="hero-content" key={currentSlide}>
          <p className="hero-subtitle" data-aos="fade-right">
            {current.label}
          </p>
          <h1 data-aos="fade-left" data-aos-delay="100">
            {current.title}
          </h1>
          <Link
            to="/shop"
            className="shop-now-link"
            data-aos="fade-right"
            data-aos-delay="200"
          >
            SHOP NOW <span>→</span>
          </Link>
        </div>
      </div>

      <LeftOutlined className="arrow-nav prev" onClick={handlePrev} />
      <RightOutlined className="arrow-nav next" onClick={handleNext} />

      <div className="carousel-nav">
        {slides.map((_, index) => (
          <div
            key={index}
            className={`dot ${index === currentSlide ? "active" : ""}`}
            onClick={() => handleDotClick(index)}
          />
        ))}
      </div>
    </HeroWrapper>
  );
};

export default HeroSection;
